// ============================================================================
// ADVOCATE HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { advocateService } from '../../api/services/advocateService';
import type {
  Advocate,
  AdvocateListItem,
  CreateAdvocateRequest,
  UpdateAdvocateRequest,
  AdvocateSearchRequest,
} from '../../api/types/advocate.types';
import type { ID } from '../../api/types';

export const useAdvocates = (params?: AdvocateSearchRequest) => {
  const [data, setData] = useState<AdvocateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1, pageSize: 20, totalPages: 0, totalItems: 0,
    hasNextPage: false, hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchItems = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await advocateService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  return { data, loading, error, pagination, refetch: fetchItems };
};

export const useAdvocate = (id: ID | undefined) => {
  const [data, setData] = useState<Advocate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchItem = useCallback(async () => {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      const response = await advocateService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchItem(); }, [fetchItem]);

  return { data, loading, error, refetch: fetchItem };
};

export const useCreateAdvocate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createAdvocate = async (
    data: CreateAdvocateRequest,
    options?: { onSuccess?: (item: Advocate) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await advocateService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { createAdvocate, loading, error };
};

export const useUpdateAdvocate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateAdvocate = async (
    id: ID, data: UpdateAdvocateRequest,
    options?: { onSuccess?: (item: Advocate) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await advocateService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { updateAdvocate, loading, error };
};

export const useDeleteAdvocate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteAdvocate = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await advocateService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { deleteAdvocate, loading, error };
};
